import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  SEARCH_FIELDS,
  UseQueryOptions,
  invariant,
  serialize,
  useQuery,
  UseMutationOptions,
  useMutation,
} from '@wyz/utils';
import { isNil, omit, omitBy } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  DebugReport,
  IBaseSearchFormSchema,
  ISearchMeta,
  ISearchTypeArgs,
  PurchaseErrorCodeKeys,
  SearchType,
} from '~/purchase/types';

type BaseQsSearchQuery = {
  searchType: SearchType;
  vehicleTypes: Array<string>;
  seasons: Array<string>;
  homologationPublicKeys?: Array<string>;
  customerPublicKey?: string;
  count: 1 | 0;
  images?: 1 | 0;
  quantity?: number;
};
type QuickQsSearchQuery = BaseQsSearchQuery & {
  mode: 'QUICK';
  identifiers?: Array<string>;
  customerPublicKey?: string;
  count: 1 | 0;
  images?: 1 | 0;
  quantity?: number;
  width?: number;
  height?: number;
  diameter?: number;
  loadIndexes?: Array<number>;
  speedIndexes?: Array<string>;
  includeHigherIndexes: 1 | 0;
};

type ManualQsSearchQuery = BaseQsSearchQuery & {
  mode: 'MANUAL';
  width: number;
  height: number;
  diameter: number;
  loadIndexes: Array<number>;
  speedIndexes: Array<string>;
  seasons: Array<string>;
  brands: Array<string>;
  includeHigherIndexes: 1 | 0;
};

type ITyreFilterQuery = QuickQsSearchQuery | ManualQsSearchQuery;

/**
 * search is allowed with at least width/height/diameter
 */
function isValidManualSearchQuery(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any,
): query is ManualQsSearchQuery {
  return !isNil(query.width) && !isNil(query.height) && !isNil(query.diameter);
}

function canRunSearch(args?: ITyreFilterQuery): boolean {
  if (
    !args ||
    Object.keys(args).length === 0 ||
    !args.vehicleTypes.length ||
    !args.seasons?.length
  ) {
    return false;
  }
  // quick search
  if (args.mode === 'QUICK') {
    return !isNil(args.identifiers?.[0]);
  }

  // manual search
  return isValidManualSearchQuery(args);
}

export function prepareSearchQuery(
  searchType: ISearchTypeArgs['searchType'],
  type: 'COUNT' | 'LIST',
  query?: IBaseSearchFormSchema,
  customerId?: string,
): ITyreFilterQuery | undefined {
  if (!query) {
    return undefined;
  }
  const baseQuery: BaseQsSearchQuery = {
    searchType: searchType,
    vehicleTypes: query.vehicleTypes,
    seasons: query.seasons,
    customerPublicKey: customerId,
    count: type === 'LIST' ? 0 : 1,
    images: type === 'LIST' ? 1 : 0,
  };
  // strict server-side filter, applied in quick and manual mode alike; empty means all (WCT-5284)
  if (query.homologations?.length) {
    baseQuery.homologationPublicKeys = query.homologations;
  }
  if (type === 'LIST') {
    baseQuery.quantity = query.quantity;
  }
  if (
    query.fieldNameSearch &&
    (
      [SEARCH_FIELDS.CAI, SEARCH_FIELDS.EAN, SEARCH_FIELDS.REFERENCE] as Array<
        typeof query.fieldNameSearch
      >
    ).includes(query.fieldNameSearch) &&
    !isNil(query.identifier?.value)
  ) {
    return {
      ...baseQuery,
      mode: 'QUICK',
      identifiers: [query.identifier?.value],
      includeHigherIndexes: query.includeHigherIndexes ? 1 : 0,
      ...(query.includeHigherIndexes
        ? omitBy(
            {
              width: query.width,
              height: query.height,
              diameter: query.diameter,
              loadIndexes: query.loadIndexes,
              speedIndexes: query.speedIndexes,
              brands: query.brands,
            },
            isNil,
          )
        : {}),
    };
  }
  const raw = query as unknown;
  if (isValidManualSearchQuery(raw)) {
    return {
      ...baseQuery,
      mode: 'MANUAL',
      width: raw.width,
      height: raw.height,
      diameter: raw.diameter,
      loadIndexes: raw.loadIndexes,
      speedIndexes: raw.speedIndexes,
      seasons: raw.seasons,
      brands: raw.brands,
      includeHigherIndexes: raw.includeHigherIndexes ? 1 : 0,
    };
  }
  return undefined;
}

type Options<TTyreOffer> = {
  count?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<number>,
      IErrorApiResponse<unknown, PurchaseErrorCodeKeys>
    >
  > & {
    args: IBaseSearchFormSchema;
  };
  list?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<TTyreOffer[], ISearchMeta>,
      IErrorApiResponse<unknown, PurchaseErrorCodeKeys, { count: number }>
    >
  > & {
    args: IBaseSearchFormSchema;
  };
  debug?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<DebugReport>,
      IErrorApiResponse<unknown, PurchaseErrorCodeKeys>
    >
  > & {
    args: IBaseSearchFormSchema;
  };
  listByEans?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<TTyreOffer[]>,
      IErrorApiResponse<unknown, PurchaseErrorCodeKeys>
    >
  > & {
    args: {
      eans: string[];
      quantity: number;
    };
  };
  refreshEan?: UseMutationOptions<
    TTyreOffer | undefined,
    IErrorApiResponse<unknown, PurchaseErrorCodeKeys>,
    { baseQuery: IBaseSearchFormSchema; ean: string; forceNoLatency?: boolean }
  >;
};

export const useManageSearch = <
  TTyreOffer,
  TRequestedKeys extends keyof Options<TTyreOffer> = keyof Options<TTyreOffer>,
  TRequestedOptions extends Options<TTyreOffer> = Pick<
    Options<TTyreOffer>,
    TRequestedKeys
  >,
>(
  options: TRequestedOptions & {
    context: { customerId: string } & ISearchTypeArgs;
  },
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const countArgs = prepareSearchQuery(
    options.context.searchType,
    'COUNT',
    options.count?.args,
    options.context.customerId,
  );
  const countQuery = useQuery({
    queryKey: PurchaseQueryKeys.search.count(
      options.context.customerId,
      options.context.searchType,
      omit(countArgs, ['quantity']),
    ),
    queryFn: () => {
      invariant(options.context.searchType);
      return callCoreApi<ISuccessApiResponse<number>>(
        `/offers/api/tenants/${tenantCode}/tyres/count?${serialize(countArgs)}`,
      );
    },
    refetchOnWindowFocus: false,
    retry: false,
    ...options.count,
    enabled: Boolean(options.count?.enabled) && canRunSearch(countArgs),
  });

  const listArgs = prepareSearchQuery(
    options.context.searchType,
    'LIST',
    options.list?.args,
    options.context.customerId,
  );
  const listQuery = useQuery({
    queryKey: PurchaseQueryKeys.search.base(
      options.context.customerId,
      options.context.searchType,
      listArgs,
    ),
    queryFn: () => {
      invariant(options.context.searchType);
      return callCoreApi<ISuccessApiResponse<Array<TTyreOffer>, ISearchMeta>>(
        `/offers/api/tenants/${tenantCode}/tyres/search?${serialize({ ...listArgs, useV2: 1 })}`,
      );
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    ...options.list,
    enabled: canRunSearch(listArgs),
  });
  const debugArgs = prepareSearchQuery(
    options.context.searchType,
    'LIST',
    options.debug?.args,
    options.context.customerId,
  );

  const debugQuery = useQuery({
    queryKey: PurchaseQueryKeys.search.base(
      options.context.customerId,
      options.context.searchType,
      debugArgs,
    ),
    queryFn: () => {
      invariant(options.context.searchType);
      return callCoreApi<ISuccessApiResponse<DebugReport>>(
        `/offers/api/tenants/${tenantCode}/tyres/search?${serialize({
          ...debugArgs,
          'useV2': 1,
          'debug-mode': true,
        })}`,
      );
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    ...options.debug,
    enabled: canRunSearch(debugArgs),
  });

  const listByEansArgs =
    options.listByEans?.args === undefined
      ? undefined
      : {
          searchType: options.context.searchType,
          identifiers: options.listByEans.args.eans,
          quantity: options.listByEans.args.quantity,
          customerPublicKey: options.context.customerId,
          useV2: 1,
        };

  const listByEansQuery = useQuery({
    queryKey: PurchaseQueryKeys.search.base(
      options.context.customerId,
      options.context.searchType,
      listByEansArgs,
    ),
    queryFn: () => {
      invariant(options.context.searchType);
      return callCoreApi<ISuccessApiResponse<Array<TTyreOffer>, ISearchMeta>>(
        `/offers/api/tenants/${tenantCode}/tyres/search?${serialize(listByEansArgs)}`,
      );
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    ...options.list,
    enabled: !isNil(listByEansArgs),
  });

  const refreshEanMutation = useMutation({
    mutationFn: ({ ean, baseQuery, forceNoLatency = true }) =>
      callCoreApi<ISuccessApiResponse<Array<TTyreOffer>>>(
        `/offers/api/tenants/${tenantCode}/tyres/search?${serialize({
          ...prepareSearchQuery(
            options.context.searchType,
            'LIST',
            {
              ...baseQuery,
              identifier: { value: ean, label: ean },
              fieldNameSearch: SEARCH_FIELDS.EAN,
            },
            options.context.customerId,
          ),
          useV2: 1,
          forceNoLatency,
        })}`,
      ).then((data) => data.data.at(0)),
    ...options.refreshEan,
  });

  return {
    count: countQuery,
    list: listQuery,
    listByEans: listByEansQuery,
    refreshEan: refreshEanMutation,
    debug: debugQuery,
  };
};
